import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../features/authentication/store/authStore';
import { UserRole } from '../features/authentication/types';
import { Box, Typography } from '@mui/material';
import { useActiveRole } from '../hooks/useActiveRole';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}

/**
 * Role-aware protected route component that checks both authentication and role permissions
 * Used to wrap routes that require specific roles
 */
export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  requiredRoles = [],
}) => {
  const { isAuthenticated } = useAuthStore();
  const { hasRole } = useActiveRole();

  // First check authentication
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If no specific roles are required, just check authentication
  if (requiredRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user has any of the required roles (role context aware)
  const hasRequiredRole = requiredRoles.some((role) => hasRole(role));

  if (!hasRequiredRole) {
    // User is authenticated but doesn't have the required role
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="error">
          Access denied. You don&apos;t have the required permissions to view this page.
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};
